/**
 * Hook to fetch portfolio sections from API
 * Used by Index (gallery) and Portfolio page
 * Solo incluye imágenes Cloudinary (para watermark)
 */

import { useState, useEffect, useMemo } from "react";
import {
  PORTFOLIO_UPDATED_EVENT,
  PORTFOLIO_UPDATED_STORAGE_KEY,
  parsePortfolioUrl,
} from "@/config/portfolioConfig";
import { isCloudinaryImage } from "@/lib/cloudinaryUtils";
import type { PortfolioSection } from "@/config/portfolioConfig";

export function usePortfolioSections(forceRefresh = false) {
  const [sections, setSections] = useState<PortfolioSection[]>([]);
  const [portfolioPublicEnabled, setPortfolioPublicEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const run = async (soft = false) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      try {
        const res = await fetch("/api/portfolio", { cache: "no-store", signal: controller.signal });
        clearTimeout(timeoutId);
        if (!res.ok) { if (!cancelled && !soft) setIsLoading(false); return; }
        const data = await res.json() as { portfolioPublicEnabled?: boolean; sections?: unknown[] };
        if (!cancelled) {
          const pub = data.portfolioPublicEnabled !== false;
          setPortfolioPublicEnabled(pub);
          if (!pub) {
            setSections([]);
          } else {
            const raw = (data.sections ?? []) as { id?: string; title?: string; urls?: unknown }[];
            setSections(raw.map((s) => ({
              id: s.id,
              title: s.title ?? "Sección",
              urls: Array.isArray(s.urls)
                ? (s.urls as unknown[]).filter((u): u is string => typeof u === "string" && u.trim().length > 0)
                : [],
            })));
          }
        }
      } catch (err) {
        clearTimeout(timeoutId);
        if (!cancelled && err instanceof Error && err.name !== "AbortError") {
          console.warn("[usePortfolioSections] Fetch error:", err.message);
        }
        if (!cancelled) setSections([]);
      } finally {
        if (!cancelled && !soft) setIsLoading(false);
      }
    };

    void run();

    const onVisibility = () => { if (!cancelled && document.visibilityState === "visible") void run(true); };
    const onPageShow = (e: PageTransitionEvent) => { if (!cancelled && e.persisted) void run(true); };
    const onStorage = (e: StorageEvent) => {
      if (!cancelled && e.key === PORTFOLIO_UPDATED_STORAGE_KEY) void run(true);
    };
    const onPortfolioUpdated = () => {
      if (!cancelled) void run(true);
    };
    // Sin polling: refetch event-driven (admin guardó) + visibility/pageshow.

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("storage", onStorage);
    window.addEventListener(PORTFOLIO_UPDATED_EVENT, onPortfolioUpdated);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(PORTFOLIO_UPDATED_EVENT, onPortfolioUpdated);
    };
  }, [forceRefresh]);

  /** URLs para galería home: solo imágenes NO horizontales (las horizontales solo en /portfolio) */
  const allImageUrls = sections
    .flatMap((s) => s.urls)
    .map((raw) => parsePortfolioUrl(raw?.trim() ?? ""))
    .filter((e) => e.url && !e.wide && isCloudinaryImage(e.url))
    .map((e) => e.url);

  /**
   * Mostrar enlace /portfolio en el header solo si:
   *  1. El admin activó el portfolio público (toggle portfolioPublicEnabled), y
   *  2. hay al menos una imagen Cloudinary en alguna sección.
   * Antes ignoraba el toggle → desactivarlo desde el admin no ocultaba el link.
   */
  const showPortfolioNavLink = useMemo(() => {
    if (isLoading) return false;
    if (!portfolioPublicEnabled) return false;
    return sections.some((s) =>
      s.urls.some((u) => {
        const p = parsePortfolioUrl(u.trim());
        return Boolean(p.url && isCloudinaryImage(p.url));
      }),
    );
  }, [isLoading, sections, portfolioPublicEnabled]);

  return { sections, isLoading, allImageUrls, showPortfolioNavLink, portfolioPublicEnabled };
}
